import { FC, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { AllRoutes } from "~/AllRoutes";
import { useLoadCurrentDiagnosticUser } from "~/hooks/useLoadCurrentDiagnosticUser";
import { useLoadCurrentUser } from "~/hooks/useLoadCurrentUser";
import { useVersionChanged } from "~/hooks/useVersionChanged";

const helmetProps = {
  titleTemplate: "%s | Conceive",
  defaultTitle: "Conceive",
};

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(AllRoutes);

export const App: FC = () => {
  const location = useLocation();
  const isDiagnosticRoute = location.pathname.startsWith("/diagnostics");

  return isDiagnosticRoute ? <DiagnosticApp /> : <MainApp />;
};

// Main app uses the current user hook
const MainApp: FC = () => {
  useVersionChanged();
  const { hasLoadedInitial } = useLoadCurrentUser();

  if (!hasLoadedInitial) return null;

  return (
    <>
      <Helmet {...helmetProps}>
        <meta charSet="utf-8" />
        <html lang="en" />
      </Helmet>
      <SentryRoutes />
    </>
  );
};

const DiagnosticApp: FC = () => {
  useVersionChanged();
  const location = useLocation();
  const { hasLoadedDiagnosticInitial } = useLoadCurrentDiagnosticUser();

  // Handle authentication flag
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get("authenticated") === "true") {
      localStorage.setItem("diagnostic_authenticated", "true");
      const newUrl = location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, [location]);

  if (!hasLoadedDiagnosticInitial) return null;

  return (
    <>
      <Helmet {...helmetProps}>
        <meta charSet="utf-8" />
        <html lang="en" />
      </Helmet>
      <SentryRoutes />
    </>
  );
};
